// [Project] > [Status] > [Attributes] - each tracked attribute's current view, and every
// earlier view with the run that recorded it. The agents write the append-only log;
// operators comment on a value here like any other part of the briefing.
// See docs/Y2026-Q3/design-deployment-dashboard.md.
import { Link, useParams } from "react-router-dom";

import {
  type AttributeDef,
  type AttributeValue,
  useProjectAttributeHistory,
} from "../api/hooks/useDashboard";
import { BriefingFrame } from "../components/BriefingFrame";
import { formatOptionValue } from "../utils/attributeFormat";
import { formatDayLong } from "../utils/formatRelative";
import { reviewAttrs } from "../utils/reviewState";
import { agentDisplayLabel } from "../utils/runParticipants";
import { runLinkPath } from "./learningAgentPaths";
import styles from "./ProjectAttributesTab.module.css";
import { WrittenBeforeBranch } from "../components/AgentSlotBar";

// "1,200 kWp", or while a choice is open "1,200 kWp (if roof A is licensed) or 820 kWp (roof B only)".
const formatAttributeValue = (
  definition: AttributeDef,
  value: AttributeValue,
): string =>
  value.options
    .map(
      (o) =>
        formatOptionValue(definition, o.value) + (o.label ? ` (${o.label})` : ""),
    )
    .join(" or ");

const RunLink = ({ entry }: { entry: AttributeValue }) =>
  entry.set_by_run_id ? (
    <>
      <Link to={runLinkPath(entry.set_by_run_id)}>View run</Link>
      <WrittenBeforeBranch at={entry.set_at} />
    </>
  ) : (
    <span className={styles.muted}>{entry.origin ?? "no run recorded"}</span>
  );

const AttributeCard = ({
  definition,
  entries,
}: {
  definition: AttributeDef;
  // Newest first, as the endpoint returns them.
  entries: AttributeValue[];
}) => {
  const current = entries[0];
  return (
    <section
      className={styles.card}
      {...reviewAttrs("attributes", definition.label, { itemId: definition.key })}
    >
      <header className={styles.cardHead}>
        <h3>{definition.label}</h3>
        {current && current.options.length > 1 && (
          <span className={styles.chip}>open choice</span>
        )}
      </header>
      <div className={styles.block}>
        {current ? (
          <>
            <p className={styles.value}>
              {formatAttributeValue(definition, current)}
            </p>
            <p className={styles.reason}>{current.reason}</p>
            <p className={styles.meta}>
              {current.origin ? (
                current.origin.charAt(0).toUpperCase() + current.origin.slice(1)
              ) : (
                <>
                  Set {formatDayLong(current.set_at)} by{" "}
                  {agentDisplayLabel(current.set_by_agent)}
                  {" · "}
                  <RunLink entry={current} />
                </>
              )}
            </p>
          </>
        ) : (
          <p className={styles.muted}>Not yet set</p>
        )}
      </div>
      {entries.length > 0 && (
        <details className={styles.history}>
          <summary>
            History ({entries.length} {entries.length === 1 ? "view" : "views"})
          </summary>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Set</th>
                <th>Value</th>
                <th>Reason</th>
                <th>By</th>
                <th>Run</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.entry_id}>
                  <td className={styles.nowrap}>
                    {formatDayLong(entry.set_at)}
                  </td>
                  <td>{formatAttributeValue(definition, entry)}</td>
                  <td>{entry.reason}</td>
                  <td className={styles.nowrap}>
                    {agentDisplayLabel(entry.set_by_agent)}
                  </td>
                  <td className={styles.nowrap}>
                    <RunLink entry={entry} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
};

export const ProjectAttributesTab = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const { data, isLoading, error } = useProjectAttributeHistory(
    decoded || null,
  );

  if (isLoading) return <p className={styles.muted}>Loading the attributes…</p>;
  if (error)
    return (
      <p className={styles.error}>
        Could not load this project&apos;s attributes.
      </p>
    );
  if (!data) return null;

  return (
    <BriefingFrame projectId={decoded} level="attributes">
      <div className={styles.tab}>
        {data.attributes.map((definition) => (
          <AttributeCard
            key={definition.key}
            definition={definition}
            entries={data.history.filter((e) => e.attribute === definition.key)}
          />
        ))}
      </div>
    </BriefingFrame>
  );
};
